import { Button, ButtonProps } from '@design-system/components/button/button';
import { Input, type InputProps } from '@design-system/components/input/input';
import { InstagramHandleInput } from '@design-system/components/input/instagramHandleInput';
import { Typography } from '@design-system/components/typography/typography';
import { Async } from '@client/new/helpers/types';
import { FormEvent, ReactNode } from 'react';
import type { RegisterStep } from '../../view-model/register.types';
import * as styles from '../register.view.css';

type StepTitleProps = {
  children: ReactNode;
};

type StepButtonProps = {
  children: ReactNode;
  type?: ButtonProps['type'];
  testId?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: ButtonProps['onClick'];
};

type StepInputSharedProps = {
  value: string;
  testId: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onValueChange: (value: string) => void;
};

type StepEmailInputProps = StepInputSharedProps & {
  variant: 'email';
  ariaLabel?: string;
  placeholder: string;
  supportingText?: string;
  error?: string;
  hideError?: boolean;
  inputWrapperClassName?: string;
  onBlur?: InputProps['onBlur'];
};

type StepTextInputProps = StepInputSharedProps & {
  variant: 'text';
  label: string;
  ariaLabel?: string;
};

type StepInstagramInputProps = StepInputSharedProps & {
  variant: 'insta';
  label: string;
  ariaLabel?: string;
  supportingText?: string;
};

type StepInputProps =
  | StepEmailInputProps
  | StepTextInputProps
  | StepInstagramInputProps;

type SignupTestIdKind = 'form' | 'input' | 'button' | 'link';
export type StepSubmitState =
  | { type: 'idle' }
  | { type: 'submitting' }
  | { type: 'error'; message: string };

type StepFormProps = {
  id: string;
  step: RegisterStep;
  onSubmit: () => void;
  children: ReactNode;
};

export const toSignupTestId = (
  step: RegisterStep,
  name: string,
  kind: SignupTestIdKind,
): string => {
  return `signup-${step}-${name}-${kind}`;
};

export const toSignupFormId = (step: RegisterStep): string => {
  return `registration-${step}-form`;
};

export const toSignupFormTestId = (step: RegisterStep): string => {
  return `signup-${step}-form`;
};

export const toStepSubmitState = <T,>(
  state: Async<T, string>,
): StepSubmitState => {
  switch (state.type) {
    case 'fetching':
      return { type: 'submitting' };
    case 'fetch-error':
      return { type: 'error', message: state.value };
    case 'not-fetched':
    case 'fetched':
      return { type: 'idle' };
  }
};

const preventDefault = (e: FormEvent<HTMLFormElement>): void => {
  e.preventDefault();
};

const sharedInputStyleProps = {
  wrapperClassName: styles.authInputRoot,
  inputWrapperClassName: styles.authInputWrapper,
} as const;

export const StepForm = (p: StepFormProps) => {
  return (
    <form
      id={p.id}
      data-testid={toSignupFormTestId(p.step)}
      className={styles.codeForm}
      noValidate
      onSubmit={(e) => {
        preventDefault(e);
        p.onSubmit();
      }}
    >
      {p.children}
    </form>
  );
};

export const StepTitle = (p: StepTitleProps) => {
  return (
    <Typography
      variant="headline"
      size="large"
      color="N-100"
      className={styles.titleText}
    >
      {p.children}
    </Typography>
  );
};

export const StepButton = (p: StepButtonProps) => {
  return (
    <Button
      type={p.type ?? 'submit'}
      color="neutral"
      size="large"
      testId={p.testId}
      loading={p.loading}
      disabled={p.disabled}
      className={styles.fullWidthButton}
      onClick={p.onClick}
    >
      {p.children}
    </Button>
  );
};

export const StepInput = (p: StepInputProps) => {
  switch (p.variant) {
    case 'email':
      return (
        <Input
          autoFocus={p.autoFocus}
          aria-label={p.ariaLabel ?? 'Email'}
          type="email"
          placeholder={p.placeholder}
          value={p.value}
          testId={p.testId}
          disabled={p.disabled}
          supportingText={p.supportingText}
          error={p.error}
          hideError={p.hideError}
          wrapperClassName={sharedInputStyleProps.wrapperClassName}
          inputWrapperClassName={
            p.inputWrapperClassName ??
            sharedInputStyleProps.inputWrapperClassName
          }
          onBlur={p.onBlur}
          onChange={(e) => p.onValueChange(e.currentTarget.value)}
        />
      );
    case 'text':
      return (
        <Input
          autoFocus={p.autoFocus}
          aria-label={p.ariaLabel ?? p.label}
          type="text"
          label={p.label}
          value={p.value}
          testId={p.testId}
          disabled={p.disabled}
          {...sharedInputStyleProps}
          onChange={(e) => p.onValueChange(e.currentTarget.value)}
        />
      );
    case 'insta':
      return (
        <InstagramHandleInput
          ariaLabel={p.ariaLabel ?? p.label}
          label={p.label}
          value={p.value}
          onValueChange={p.onValueChange}
          testId={p.testId}
          autoFocus={p.autoFocus}
          supportingText={p.supportingText}
          disabled={p.disabled}
          {...sharedInputStyleProps}
        />
      );
  }
};
